import type { CatalogVariant } from '@numistic/contracts'
import { DataError, useCreateVariant, useRemoveVariant, useUpdateVariant } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Field, Input } from '@numistic/ui'
import { useState } from 'react'

import { MutationErrors } from '../components/mutation-errors.js'
import { useErrorText } from '../lib/errors.js'
import { MetaForm } from '../meta/meta-form.js'

export function VariantsEditor({
  itemId,
  typeCode,
  variants,
}: {
  itemId: string
  typeCode: string
  variants: CatalogVariant[]
}) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<CatalogVariant | 'new' | null>(null)
  const remove = useRemoveVariant(itemId)

  if (editing)
    return (
      <VariantForm
        key={editing === 'new' ? 'new' : editing.id}
        itemId={itemId}
        typeCode={typeCode}
        variant={editing === 'new' ? undefined : editing}
        onDone={() => setEditing(null)}
      />
    )

  return (
    <div className="grid gap-3">
      {variants.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('catalog.noVariants')}</p>
      ) : null}
      <ul className="grid gap-2">
        {variants.map((v) => (
          <li key={v.id} className="flex items-center gap-2 rounded-lg border p-3">
            <span className="mr-auto font-medium">{v.label}</span>
            <Button variant="ghost" size="sm" onClick={() => setEditing(v)}>
              {t('common.edit')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => remove.mutate(v.id)}>
              {t('common.remove')}
            </Button>
          </li>
        ))}
      </ul>
      <MutationErrors errors={[remove.error]} />
      <Button variant="outline" className="justify-self-start" onClick={() => setEditing('new')}>
        {t('catalog.variantAdd')}
      </Button>
    </div>
  )
}

function VariantForm({
  itemId,
  typeCode,
  variant,
  onDone,
}: {
  itemId: string
  typeCode: string
  variant?: CatalogVariant
  onDone: () => void
}) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const [label, setLabel] = useState(variant?.label ?? '')
  const create = useCreateVariant(itemId)
  const update = useUpdateVariant(itemId)
  const error = create.error ?? update.error
  return (
    <div className="grid gap-4">
      <Field
        label={t('catalog.variantLabel')}
        error={label.trim() ? undefined : t('errors.validation.required')}
      >
        <Input value={label} onChange={(e) => setLabel(e.target.value)} />
      </Field>
      <MetaForm
        typeCode={typeCode}
        level="variant"
        defaultValues={variant?.values}
        serverIssues={error instanceof DataError ? error.issues : undefined}
        footer={
          <div className="grid gap-2">
            {error ? <Alert variant="destructive">{errorText.request(error)}</Alert> : null}
            <Button type="button" variant="ghost" className="justify-self-start" onClick={onDone}>
              {t('common.cancel')}
            </Button>
          </div>
        }
        pending={create.isPending || update.isPending}
        onSubmit={(values) => {
          if (!label.trim()) return
          if (variant)
            update.mutate(
              { id: variant.id, version: variant.version, label, values },
              { onSuccess: onDone },
            )
          else create.mutate({ label, values }, { onSuccess: onDone })
        }}
      />
    </div>
  )
}
